import React, { useState, useEffect } from 'react'
import { Template } from '@templatean/shared/types'
import { templateService } from '@templatean/shared/services'
import { LoadingSpinner } from '@templatean/shared/components'
import { formatDate } from '@templatean/shared/utils'
import { PowerPointService } from '@templatean/shared/services/office.service'
import { useOffice } from '@templatean/shared/hooks'

const SlideLibrary: React.FC = () => {
  const { service } = useOffice()
  const [templates, setTemplates] = useState<Template[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('presentation')

  const powerPointService = service as PowerPointService

  useEffect(() => {
    loadTemplates()
  }, [selectedCategory])

  const loadTemplates = async () => {
    try {
      setLoading(true)
      const response = await templateService.getTemplates({
        category: selectedCategory as any,
        isPublished: true,
        pageSize: 50
      })
      // Filter for PowerPoint-compatible templates
      const pptTemplates = response.items.filter(t => 
        t.category === 'presentation' || 
        t.content.format === 'pptx' ||
        t.metadata.powerPointCompatible
      )
      setTemplates(pptTemplates)
    } catch (err) {
      setError('Failed to load templates')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleInsertSlide = async (template: Template) => {
    try {
      // Parse template content
      const slideData = JSON.parse(template.content.body)
      
      // Insert slide
      await powerPointService.insertSlide(slideData.layout)
      
      // Add title if present
      if (slideData.title) {
        await powerPointService.insertTextBox(slideData.title, {
          fontSize: 44,
          bold: true,
          position: { x: 50, y: 50 }
        })
      }
      
      // Add content based on slide type
      if (slideData.bullets && slideData.bullets.length > 0) {
        const bulletText = slideData.bullets.map((b: string) => `• ${b}`).join('\n')
        await powerPointService.insertTextBox(bulletText, {
          fontSize: 24,
          position: { x: 50, y: 150 }
        })
      }
      
      // Add images if present
      if (slideData.images && slideData.images.length > 0) {
        for (const image of slideData.images) {
          await powerPointService.insertImage(image.base64, {
            width: image.width,
            height: image.height,
            position: image.position
          })
        }
      }
      
      alert(`Slide "${template.name}" inserted successfully!`)
    } catch (error) {
      console.error('Failed to insert slide:', error)
      setError('Failed to insert slide')
    }
  }

  const slideCategories = [
    { value: 'presentation', label: 'All Presentations' },
    { value: 'title', label: 'Title Slides' },
    { value: 'content', label: 'Content Slides' },
    { value: 'comparison', label: 'Comparison' },
    { value: 'timeline', label: 'Timelines' },
    { value: 'process', label: 'Process Flows' },
    { value: 'chart', label: 'Charts & Graphs' },
    { value: 'team', label: 'Team Slides' },
    { value: 'conclusion', label: 'Conclusions' },
  ]

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (loading) return <LoadingSpinner />
  if (error) return <div className="error-message">{error}</div>

  return (
    <div className="slide-library">
      <h3>Slide Templates</h3>
      
      <div className="template-filters">
        <input
          type="text"
          placeholder="Search slides..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
        
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="category-select"
        >
          {slideCategories.map(cat => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
      </div>

      <div className="slide-grid">
        {filteredTemplates.map((template) => (
          <div key={template.id} className="slide-card">
            <div className="slide-preview">
              {template.metadata.thumbnail ? (
                <img 
                  src={template.metadata.thumbnail} 
                  alt={template.name}
                  className="slide-thumbnail"
                />
              ) : (
                <div className="slide-placeholder">
                  <span className="slide-icon">🎯</span>
                </div>
              )}
            </div>
            
            <div className="slide-info">
              <h4 className="slide-name">{template.name}</h4>
              {template.description && (
                <p className="slide-description">{template.description}</p>
              )}
              
              <div className="slide-meta">
                <span className="slide-category">
                  {template.metadata.slideType || template.category}
                </span>
                <span className="slide-date">
                  {formatDate(template.updatedAt)}
                </span>
              </div>
              
              {template.tags.length > 0 && (
                <div className="slide-tags">
                  {template.tags.map((tag) => (
                    <span key={tag} className="tag">{tag}</span>
                  ))}
                </div>
              )}
              
              <div className="slide-features">
                {template.metadata.hasAnimations && (
                  <span className="feature">✨ Animations</span>
                )}
                {template.metadata.hasTransitions && (
                  <span className="feature">🔄 Transitions</span>
                )}
                {template.metadata.hasCharts && (
                  <span className="feature">📊 Charts</span>
                )}
                {template.metadata.hasImages && (
                  <span className="feature">🖼️ Images</span>
                )}
              </div>
              
              <button
                onClick={() => handleInsertSlide(template)}
                className="btn btn-primary btn-sm"
              >
                Insert Slide
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="empty-state">
          <p>No slide templates found</p>
        </div>
      )}
    </div>
  )
}

export default SlideLibrary